import { apiFetchJson, apiPost, apiPatch, apiDelete } from './api';

/**
 * Fetch integration details including all revisions
 * @param {string} integrationId - Integration UUID
 * @returns {Promise<Object>} Integration details with revisions
 */
export const fetchIntegrationDetails = async (integrationId) => {
  const [integration, revisions] = await Promise.all([
    apiFetchJson(`/api/data-loads/integrations/${integrationId}/`),
    apiFetchJson(`/api/data-loads/integrations/${integrationId}/revisions/`),
  ]);

  return {
    integration,
    revisions: Array.isArray(revisions) ? revisions : [],
  };
};

/**
 * Fetch all integrations with optional filtering
 * @param {Object} params - Query parameters
 * @returns {Promise<Array>} Array of integration objects
 */
export const fetchIntegrations = async (params = {}) => {
  const queryParams = new URLSearchParams();

  if (params.client) queryParams.append('client', params.client);
  if (params.is_active !== undefined) queryParams.append('is_active', params.is_active);
  if (params.content_type) queryParams.append('content_type', params.content_type);

  return apiFetchJson(`/api/data-loads/integrations/?${queryParams.toString()}`);
};

/**
 * Create a new integration
 * @param {Object} integrationData - Integration data
 * @returns {Promise<Object>} Created integration object
 */
export const createIntegration = async (integrationData) => {
  return apiPost('/api/data-loads/integrations/', integrationData);
};

/**
 * Update an existing integration
 * @param {string} integrationId - Integration UUID
 * @param {Object} integrationData - Updated integration data
 * @returns {Promise<Object>} Updated integration object
 */
export const updateIntegration = async (integrationId, integrationData) => {
  return apiPatch(`/api/data-loads/integrations/${integrationId}/`, integrationData);
};

/**
 * Delete (deactivate) an integration
 * @param {string} integrationId - Integration UUID
 * @returns {Promise<Object>} Response message
 */
export const deleteIntegration = async (integrationId) => {
  return apiDelete(`/api/data-loads/integrations/${integrationId}/`);
};

/**
 * Fetch integration logs (load events)
 * @param {string} integrationId - Integration UUID
 * @returns {Promise<Array>} Array of log/load event objects
 */
export const fetchIntegrationLogs = async (integrationId) => {
  return apiFetchJson(`/api/data-loads/integrations/${integrationId}/load_events/`);
};
